import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import {
  PageContainer,
  HeaderContainer,
  Title,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  AddUserButton,
} from "./TablaActivos.styles";
import { envasesService, CatalogosEnvase } from "../../services/envasesService";
import { CreaEnvaseDto } from "../../dtos/Envase";
import { ApiError } from "../../services/apiError";
import ProductoAutocomplete from "./ProductoAutocomplete";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado." };
};

const emptyForm: CreaEnvaseDto = {
  numeroSerie: "",
  pesoTara: null,
  evidencia: "",
  codigoProducto: null,
  fabricanteId: "",
  modeloId: "",
  idTipoEnvase: null,
  codigoCuerpo: "",
  codigoCapuchon: "",
  codigoCuello: "",
  codigoEstarcido: "",
  codigoOjiva: "",
  idValvula: null,
};

const RegistroEnvase: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreaEnvaseDto>({ ...emptyForm });
  const [catalogos, setCatalogos] = useState<CatalogosEnvase | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  useEffect(() => {
    envasesService
      .loadCatalogosEnvase()
      .then(setCatalogos)
      .catch((err) => notify(handleApiError(err).message, "error", 3000))
      .finally(() => setLoadingCatalogos(false));
  }, []);

  const modelosFiltrados =
    catalogos?.modelos.filter(
      (m) => !form.fabricanteId || m.fabricanteId === form.fabricanteId,
    ) ?? [];

  const handleSubmit = async () => {
    if (!form.numeroSerie?.trim()) {
      notify("El número de serie es obligatorio.", "warning", 2500);
      return;
    }
    setLoading(true);
    try {
      const payload: CreaEnvaseDto = {
        numeroSerie: form.numeroSerie.trim(),
        pesoTara: form.pesoTara ?? null,
        evidencia: form.evidencia || null,
        codigoProducto: form.codigoProducto || null,
        fabricanteId: form.fabricanteId || null,
        modeloId: form.modeloId || null,
        idTipoEnvase: form.idTipoEnvase || null,
        codigoCuerpo: form.codigoCuerpo || null,
        codigoCapuchon: form.codigoCapuchon || null,
        codigoCuello: form.codigoCuello || null,
        codigoEstarcido: form.codigoEstarcido || null,
        codigoOjiva: form.codigoOjiva || null,
        idValvula: form.idValvula || null,
      };
      await envasesService.createEnvase(payload);
      notify("Envase registrado correctamente.", "success", 2500);
      navigate("/consulta-activos");
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  const setField = (field: keyof CreaEnvaseDto, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loadingCatalogos || !catalogos) {
    return (
      <PageContainer>
        <Title>Cargando catálogos...</Title>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ marginBottom: 15 }}>
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate("/consulta-activos")}
        />
      </div>
      <HeaderContainer>
        <Title>Registrar envase</Title>
      </HeaderContainer>

      <FilterBar style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        <Field>
          <Label>Número de serie *</Label>
          <StyledInput
            value={form.numeroSerie ?? ""}
            onChange={(e) => setField("numeroSerie", e.target.value)}
          />
        </Field>
        <Field>
          <Label>Peso tara (kg)</Label>
          <StyledInput
            type="number"
            value={form.pesoTara ?? ""}
            onChange={(e) =>
              setField("pesoTara", e.target.value === "" ? null : Number(e.target.value))
            }
          />
        </Field>
        <Field>
          <Label>Código producto</Label>
          <ProductoAutocomplete
            value={form.codigoProducto ?? null}
            onChange={(codigo) => setField("codigoProducto", codigo)}
          />
        </Field>
        <Field>
          <Label>Fabricante</Label>
          <StyledSelect
            value={form.fabricanteId ?? ""}
            onChange={(e) => {
              setField("fabricanteId", e.target.value);
              setField("modeloId", "");
            }}
          >
            <option value="">Selecciona...</option>
            {catalogos.fabricantes.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Modelo</Label>
          <StyledSelect
            value={form.modeloId ?? ""}
            onChange={(e) => setField("modeloId", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {modelosFiltrados.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Tipo de envase</Label>
          <StyledSelect
            value={form.idTipoEnvase ?? ""}
            onChange={(e) =>
              setField(
                "idTipoEnvase",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          >
            <option value="">Selecciona...</option>
            {catalogos.tiposEnvase.map((t) => (
              <option key={t.idTipoEnvase} value={t.idTipoEnvase}>
                {t.tipoDeEnvase || t.descripcion}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Cuerpo</Label>
          <StyledSelect
            value={form.codigoCuerpo ?? ""}
            onChange={(e) => setField("codigoCuerpo", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {catalogos.cuerpos.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} {c.color ? `- ${c.color}` : ""}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Capuchón</Label>
          <StyledSelect
            value={form.codigoCapuchon ?? ""}
            onChange={(e) => setField("codigoCapuchon", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {catalogos.capuchones.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} {c.color ? `- ${c.color}` : ""}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Cuello</Label>
          <StyledSelect
            value={form.codigoCuello ?? ""}
            onChange={(e) => setField("codigoCuello", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {catalogos.cuellos.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} {c.color ? `- ${c.color}` : ""}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Estarcido</Label>
          <StyledSelect
            value={form.codigoEstarcido ?? ""}
            onChange={(e) => setField("codigoEstarcido", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {catalogos.estarcidos.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} {c.color ? `- ${c.color}` : ""}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Ojiva</Label>
          <StyledSelect
            value={form.codigoOjiva ?? ""}
            onChange={(e) => setField("codigoOjiva", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {catalogos.ojivas.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} {c.color ? `- ${c.color}` : ""}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Válvula</Label>
          <StyledSelect
            value={form.idValvula ?? ""}
            onChange={(e) =>
              setField(
                "idValvula",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          >
            <option value="">Selecciona...</option>
            {catalogos.valvulas.map((v) => (
              <option key={v.idValvula} value={v.idValvula}>
                {v.descripcion}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field style={{ gridColumn: "1 / -1" }}>
          <Label>Evidencia (URL)</Label>
          <StyledInput
            value={form.evidencia ?? ""}
            onChange={(e) => setField("evidencia", e.target.value)}
          />
        </Field>
        <AddUserButton onClick={handleSubmit} disabled={loading}>
          {loading ? "Guardando..." : "Registrar envase"}
        </AddUserButton>
      </FilterBar>
    </PageContainer>
  );
};

export default RegistroEnvase;
